import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Phone, Mail, MapPin, Trash2, Edit, ChevronRight, Users,
  UserCheck, UserPlus, Truck, FileCheck, Bell, StickyNote,
} from "lucide-react";

const categoryConfig = {
  "Kunde": { icon: UserCheck, color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300", accent: "border-l-emerald-500" },
  "Interessent": { icon: UserPlus, color: "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300", accent: "border-l-blue-500" },
  "Lieferant": { icon: Truck, color: "bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300", accent: "border-l-purple-500" },
};

export const CustomerCard = ({ customer, hasReminder, onOpen, onEdit, onDelete }) => {
  const catConfig = categoryConfig[customer.category] || categoryConfig["Kunde"];
  const CategoryIcon = catConfig.icon;
  const hasNotes = customer.notes && customer.notes.trim() !== "";
  return (
    <Card
      className={`card-hover cursor-pointer border-l-4 ${catConfig.accent}`}
      onClick={() => onOpen(customer)}
      data-testid={`customer-card-${customer.id}`}
    >
      <CardContent className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
          <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${catConfig.color.split(" ").slice(0, 2).join(" ")}`}>
              <CategoryIcon className={`w-5 h-5 sm:w-6 sm:h-6 ${catConfig.color.split(" ").slice(2).join(" ")}`} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-base sm:text-lg leading-tight line-clamp-2 break-words">
                {customer.company_name}
              </h3>
              <div className="flex items-center gap-2 flex-wrap mt-1">
                <Badge variant="outline" className={`text-xs ${catConfig.color}`}>
                  {customer.category || "Kunde"}
                </Badge>
                {customer.has_maintenance_contract && (
                  <Badge variant="secondary" className="text-xs gap-1">
                    <FileCheck className="w-3 h-3" />
                    Wartung
                  </Badge>
                )}
                {hasNotes && (
                  <Badge variant="outline" className="text-xs gap-1 bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700">
                    <StickyNote className="w-3 h-3" />
                    Notizen
                  </Badge>
                )}
                {hasReminder && (
                  <Badge variant="destructive" className="text-xs gap-1">
                    <Bell className="w-3 h-3" />
                    Erinnerung
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">
                {customer.city && (
                  <span className="flex items-center gap-1 min-w-0">
                    <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{customer.postal_code} {customer.city}</span>
                  </span>
                )}
                {customer.phone && (
                  <span className="flex items-center gap-1 min-w-0">
                    <Phone className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{customer.phone}</span>
                  </span>
                )}
                {customer.email && (
                  <span className="flex items-center gap-1 min-w-0 max-w-full">
                    <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{customer.email}</span>
                  </span>
                )}
              </div>
              {customer.contacts && customer.contacts.length > 0 && (
                <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground">
                  <Users className="w-3.5 h-3.5" />
                  {customer.contacts.length} Ansprechpartner
                </div>
              )}
            </div>
          </div>
          <div className="flex items-stretch sm:items-center justify-end gap-2 sm:gap-1 shrink-0 mt-1 pt-3 border-t border-border sm:mt-0 sm:pt-0 sm:border-t-0">
            <Button
              variant="ghost"
              className="flex-1 sm:flex-none h-10 sm:h-9 sm:w-9 px-3 sm:px-0 gap-1.5 border border-border sm:border-0"
              onClick={(e) => { e.stopPropagation(); onEdit(customer); }}
              data-testid={`edit-customer-${customer.id}`}
            >
              <Edit className="w-4 h-4" />
              <span className="sm:hidden">Bearbeiten</span>
            </Button>
            <Button
              variant="ghost"
              className="flex-1 sm:flex-none h-10 sm:h-9 sm:w-9 px-3 sm:px-0 gap-1.5 border border-border sm:border-0 text-destructive hover:text-destructive"
              onClick={(e) => onDelete(customer, e)}
              data-testid={`delete-customer-${customer.id}`}
            >
              <Trash2 className="w-4 h-4" />
              <span className="sm:hidden">Löschen</span>
            </Button>
            <ChevronRight className="w-5 h-5 text-muted-foreground hidden sm:block" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
